import { FC, useState } from 'react';
import { getQueryKey } from '@trpc/react-query';
import { useHistory } from 'react-router-dom';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MailOutlined from '@mui/icons-material/MailOutlined';
import FileDownload from '@mui/icons-material/FileDownload';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  LpInvestorUpdateStatus,
  LpInvestorUpdateType,
} from 'further-types/lp/investor-update';
import { SortOrder, CalendarDayRange } from 'further-types/utils';
import { RolePermission } from 'further-types/admin-user-role';
import { CalendarDay } from 'further-ui/utils';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import Menu from 'further-ui/components/Menu';
import { Column } from 'further-ui/components/Layout';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import SearchField from 'further-ui/components/SearchField';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import {
  PERIOD_END_REPORT_LIST,
  createPeriodEndReportEditRoute,
  BULLETIN_UPDATE_CREATE,
  createBulletinUpdateEditRoute,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import DownloadInvestorUpdates from './DownloadInvestorUpdates';

const pageId = 'lp-investor-updates';
const orderByOptions = ['reportDate'] as const;
const lpInvestorUpdateTypeLabels = {
  [LpInvestorUpdateType.BulletinUpdate]: 'Bulletin update',
  [LpInvestorUpdateType.PeriodEndReport]: 'Period-end report',
};

type InvestorUpdate = {
  id: string;
  title: string;
  status: LpInvestorUpdateStatus;
  type: LpInvestorUpdateType;
  reportDate?: CalendarDay;
  investorPdfUploads: Array<{
    investorId: string;
    path: string;
    previewUrl: string;
  }>;
  investors: Array<{
    id: string;
    fullName: string;
  }>;
};

const InvestorUpdates: FC = () => {
  const history = useHistory();
  const { selectedVehicleId } = useLayoutContext();
  const theme = useTheme();
  const deleteDisclosure = useDisclosure<{ id: string }>();
  const [investorUpdate, setInvestorUpdate] = useState<InvestorUpdate | null>(
    null,
  );

  const [canCreate, canEdit, canDelete, canRead] = useHasPermissions([
    RolePermission.LpCreateInvestorUpdate,
    RolePermission.LpEditInvestorUpdate,
    RolePermission.LpDeleteInvestorUpdate,
    RolePermission.LpReadInvestorUpdates,
  ]);

  const { filters, handleChangeFilters, pagination, isFiltersChangePending } =
    usePaginatedFilters<
      { search: string; dateRange: CalendarDayRange | undefined },
      (typeof orderByOptions)[number]
    >(pageId, {
      defaultFilters: {
        search: '',
        dateRange: undefined,
      },
      defaultOrder: {
        orderBy: 'reportDate',
        order: SortOrder.Desc,
      },
      orderByOptions,
    });

  const [{ results, totalCount }] = api.lpInvestorUpdate.list.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    page: pagination.properties.page,
    rowsPerPage: pagination.properties.rowsPerPage,
    order: pagination.properties.order,
    orderBy: pagination.properties.orderBy,
    search: filters.search || undefined,
    startDate: filters.dateRange?.startDate ?? undefined,
    endDate: filters.dateRange?.endDate ?? undefined,
  });

  const columns: Array<TableColumn<InvestorUpdate>> = [
    {
      label: 'Report name',
      key: 'title',
      render: ({ title }) => <Typography variant="h6">{title}</Typography>,
    },
    {
      label: 'Publication date',
      key: 'reportDate',
      render: ({ reportDate }) => (
        <Typography variant="h6" color={reportDate ? 'grey.900' : 'grey.500'}>
          {reportDate?.toReadable() ?? 'N/A'}
        </Typography>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: ({ status }) => {
        const isPublished = status === LpInvestorUpdateStatus.Published;
        return (
          <ColorIndicatorWithText
            color={
              isPublished
                ? theme.palette.success.light
                : theme.palette.warning.light
            }
            text={isPublished ? 'Published' : 'Draft'}
            spacing="xxs"
            rounded
          />
        );
      },
    },
    {
      label: 'Update type',
      key: 'type',
      render: ({ type }) => lpInvestorUpdateTypeLabels[type],
    },
  ];

  const deleteInvestorUpdate = api.lpInvestorUpdate.delete.useMutation(
    useHandleTrpcMutation()({
      onSuccess: deleteDisclosure.onClose,
      evictions: [
        getQueryKey(api.periodEndReport.getById),
        getQueryKey(api.bulletinUpdate.getById),
      ],
    }),
  );

  const handleDeleteInvestorUpdate = () => {
    deleteInvestorUpdate.mutate({
      vehicleId: selectedVehicleId ?? '',
      id: deleteDisclosure.actionProps.id,
    });
  };

  return (
    <Page
      tabTitle="Investor Updates"
      spacing="lg"
      heading="Investor Updates"
      headingIcon={MailOutlined}
      withHeadingDivider
    >
      <Column spacing="sm">
        <PageFilters
          customActions={
            canCreate ? (
              <Menu
                label="Create new update"
                buttonVariant="contained"
                alignWithIconRightEdge
                transparentBackdrop={false}
                gap="xs"
                items={[
                  {
                    label:
                      lpInvestorUpdateTypeLabels[
                        LpInvestorUpdateType.BulletinUpdate
                      ],
                    icon: StickyNote2Outlined,
                    onClick: () => {
                      history.push(BULLETIN_UPDATE_CREATE);
                    },
                  },
                  {
                    label:
                      lpInvestorUpdateTypeLabels[
                        LpInvestorUpdateType.PeriodEndReport
                      ],
                    icon: CalendarMonthOutlined,
                    onClick: () => {
                      history.push(PERIOD_END_REPORT_LIST);
                    },
                  },
                ]}
              />
            ) : undefined
          }
        >
          <Filter size="md">
            <SearchField
              autoSearchOnDebounce
              name="search"
              placeholder="Search by report name"
              fullWidth
              value={filters.search}
              onSearch={(search) => {
                handleChangeFilters({ search });
              }}
              allowEmptyQuery
              iconPlacement="right"
            />
          </Filter>
          <Filter size="md">
            <DateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters({ dateRange });
              }}
              placeholder="Select date range"
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          pagination={pagination}
          isLoading={isFiltersChangePending || pagination.isPaginationChanging}
          rows={results}
          rowsTotalCount={totalCount}
          emptyMessage="No investor updates found."
          disableSorting
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              hidden: !canEdit,
              onClick: ({ id, type }) => {
                if (type === LpInvestorUpdateType.PeriodEndReport) {
                  history.push(createPeriodEndReportEditRoute(id));
                } else {
                  history.push(createBulletinUpdateEditRoute(id));
                }
              },
            },
            {
              label: 'Download reports',
              icon: FileDownload,
              hidden: ({ type, status }) =>
                type === LpInvestorUpdateType.BulletinUpdate ||
                status !== LpInvestorUpdateStatus.Published ||
                !canRead,
              onClick: setInvestorUpdate,
            },
            {
              label: 'Delete',
              icon: Delete,
              hidden: !canDelete,
              onClick: ({ id }) => {
                deleteDisclosure.stageAction({ id });
              },
            },
          ]}
        />
      </Column>
      {investorUpdate ? (
        <DownloadInvestorUpdates
          investorUpdate={investorUpdate}
          vehicleId={selectedVehicleId ?? ''}
          reportId={investorUpdate.id}
          onClose={() => setInvestorUpdate(null)}
        />
      ) : null}
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Delete investor update"
        content="This action cannot be undone and all information relating to this update will be lost."
        actions={{
          confirm: {
            label: 'Delete investor update',
            onClick: handleDeleteInvestorUpdate,
          },
          cancel: {
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

export default InvestorUpdates;
